import { Save } from 'lucide-react';
import { useState, useEffect } from 'react';
import Button from '../../../components/Button';
import ErrorDisplay from '../../../components/ErrorDisplay';
import { OnboardingProgress, type ObsessLessUser } from '../../../types/commonTypes';
import { formatDateTime } from '../../../utils/timeUtils';
import { userProfileService } from '../services/userProfileService';
import { DeviceInfoCard } from './DeviceInfoCard';

interface UserProfileTabProps {
    currentUser: ObsessLessUser;
    onUserUpdate: () => Promise<void>;
}

export const UserProfileTab = ({ currentUser, onUserUpdate }: UserProfileTabProps) => {
    const [selectedOnboardingProgress, setSelectedOnboardingProgress] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // undefined while loading, null when the Auth user has no name.
    const [authDisplayName, setAuthDisplayName] = useState<string | null | undefined>(undefined);
    const [authDisplayNameError, setAuthDisplayNameError] = useState<string | null>(null);

    useEffect(() => {
        if (currentUser && 'onboardingProgress' in currentUser) {
            setSelectedOnboardingProgress(currentUser.onboardingProgress);
        } else {
            setSelectedOnboardingProgress('');
        }

        setError(null);
        setSuccessMessage(null);
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser?.uid) {
            return;
        }

        let isCurrent = true;
        setAuthDisplayName(undefined);
        setAuthDisplayNameError(null);

        userProfileService.getAuthDisplayName(currentUser.uid)
            .then((name) => {
                if (isCurrent) setAuthDisplayName(name);
            })
            .catch((err: unknown) => {
                if (!isCurrent) return;
                setAuthDisplayName(null);
                setAuthDisplayNameError(err instanceof Error ? err.message : 'Failed to load the Auth name');
            });

        return () => {
            isCurrent = false;
        };
    }, [currentUser?.uid]);

    const handleSave = async () => {
        if (!currentUser || !selectedOnboardingProgress) {
            setError('Please select an onboarding status');
            return;
        }

        setIsSaving(true);
        setError(null);
        setSuccessMessage(null);

        try {
            await userProfileService.updateOnboardingStatus(
                currentUser.uid,
                selectedOnboardingProgress
            );
            setSuccessMessage('Onboarding status updated successfully!');
            await onUserUpdate();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update onboarding status');
        } finally {
            setIsSaving(false);
        }
    };

    const onboardingProgressOptions = Object.values(OnboardingProgress);

    return (
        <div className="flex flex-col gap-4">
            {/* User Info Section */}
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">User Information</h2>
                <div className="text-sm text-gray-800 space-y-2">
                    <p className="mb-2">
                        User ID: <span className="font-bold break-all">{currentUser?.uid}</span>
                    </p>
                    <p className="mb-2">
                        User Name (profile): <span className="font-bold">{currentUser?.displayName}</span>
                    </p>
                    <p className="mb-2">
                        User Name (Firebase Auth):{' '}
                        {authDisplayNameError ? (
                            <span className="text-red-600">{authDisplayNameError}</span>
                        ) : authDisplayName === undefined ? (
                            <span className="text-gray-500">Loading...</span>
                        ) : (
                            <span className="font-bold">{authDisplayName ?? <span className="font-normal text-gray-500">None</span>}</span>
                        )}
                    </p>
                    <p className="mb-2">
                        User Email: <span className="font-bold break-all">{currentUser?.email}</span>
                    </p>

                    {('createdAt' in currentUser) && currentUser.createdAt && (
                        <p className="mb-2">
                            Created At: <span className="font-bold">{formatDateTime(currentUser.createdAt)}</span>
                        </p>
                    )}

                    {('hasFinishedOnboarding' in currentUser) && (
                        <p className="mb-2">
                            Has Finished Onboarding:
                            <span className={`font-bold ml-2 ${currentUser.hasFinishedOnboarding ? 'text-green-600' : 'text-red-600'}`}>
                                {currentUser.hasFinishedOnboarding ? 'Yes' : 'No'}
                            </span>
                        </p>
                    )}

                    {currentUser.referralCode && (
                        <p className="mb-2">
                            Referral Code: <span className="font-bold">{currentUser.referralCode}</span>
                        </p>
                    )}
                </div>
            </div>

            <DeviceInfoCard deviceInfo={currentUser.deviceInfo} timezone={currentUser.timezone} />

            {/* Quest Journey Section */}
            {currentUser.questJourney && (
                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Quest Journey</h3>
                    <div className="text-sm text-gray-800 space-y-2">
                        <p className="mb-1">
                            Current Day: <span className="font-bold">{currentUser.questJourney.currentDay}</span>
                        </p>
                        <p className="mb-1">
                            Is Current Day Completed:
                            <span className={`font-bold ml-2 ${currentUser.questJourney.isCurrentDayCompleted ? 'text-green-600' : 'text-red-600'}`}>
                                {currentUser.questJourney.isCurrentDayCompleted ? 'Yes' : 'No'}
                            </span>
                        </p>
                        {currentUser.questJourney.journeyDayCompletedAt && (
                            <p className="mb-1">
                                Journey Day Completed At: <span className="font-bold">{formatDateTime(currentUser.questJourney.journeyDayCompletedAt)}</span>
                            </p>
                        )}
                        {currentUser.questJourney.journeyDayStartedAt && (
                            <p className="mb-1">
                                Journey Day Started At: <span className="font-bold">{formatDateTime(currentUser.questJourney.journeyDayStartedAt)}</span>
                            </p>
                        )}
                        {currentUser.questJourney.createdAt && (
                            <p className="mb-1">
                                Created At: <span className="font-bold">{formatDateTime(currentUser.questJourney.createdAt)}</span>
                            </p>
                        )}
                        {currentUser.questJourney.updatedAt && (
                            <p className="mb-1">
                                Updated At: <span className="font-bold">{formatDateTime(currentUser.questJourney.updatedAt)}</span>
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Onboarding Status Section */}
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Onboarding Status</h2>

                {error && <ErrorDisplay message={error} />}
                {successMessage && (
                    <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                        {successMessage}
                    </div>
                )}

                <div className="mb-4">
                    <label htmlFor="onboarding-progress" className="block text-sm font-medium text-gray-700 mb-2">
                        Onboarding Progress
                    </label>
                    <select
                        id="onboarding-progress"
                        value={selectedOnboardingProgress}
                        onChange={(e) => setSelectedOnboardingProgress(e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="">Select onboarding status...</option>
                        {onboardingProgressOptions.map((option) => (
                            <option key={option} value={option}>
                                {option.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                    <Button
                        icon={<Save className="w-4 h-4" />}
                        onClick={handleSave}
                        disabled={isSaving || !selectedOnboardingProgress}
                        variant="primary"
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>

                    {selectedOnboardingProgress && (
                        <div className="text-sm text-gray-600">
                            <p>
                                Will set: <span className="font-semibold">hasFinishedOnboarding = {
                                    selectedOnboardingProgress === OnboardingProgress.FinishedOnboarding ? 'true' : 'false'
                                }</span>
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
